"use client";

import { Tooltip as BaseTooltip } from "@base-ui/react/tooltip";
import {
  createContext,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentProps,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from "react";
import { Kbd } from "./kbd";
import styles from "./tooltip.module.css";

const TriggerContext = createContext(false);
export const useHasTooltip = () => useContext(TriggerContext);

export function TooltipProvider(props: ComponentProps<typeof BaseTooltip.Provider>) {
  return <BaseTooltip.Provider delay={400} closeDelay={80} timeout={400} {...props} />;
}

export type TooltipProps = {
  content: ReactNode;
  shortcuts?: readonly (readonly string[])[];
  shortcutAria?: string | undefined;
  side?: "top" | "bottom" | "left" | "right";
  align?: "start" | "center" | "end";
  disabled?: boolean;
  onlyWhenTruncated?: boolean;
  children: ReactElement;
};

/** A single trigger, with no additional DOM wrapper or change to its semantics. */
export function Tooltip({
  content,
  shortcuts,
  shortcutAria,
  side = "top",
  align = "center",
  disabled = false,
  onlyWhenTruncated = false,
  children,
}: TooltipProps) {
  const trigger = useRef<HTMLElement>(null);
  const popup = useRef<HTMLDivElement>(null);
  const pointerTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const dismissed = useRef(false);
  const descriptionId = useId();
  const [open, setOpen] = useState(false);
  const [pageScale, setPageScale] = useState(1);
  const [override, setOverride] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const hasContent =
    content !== undefined && content !== null && content !== "" && content !== false;
  const childDescription = (children.props as { "aria-describedby"?: string })["aria-describedby"];
  const describedBy = [childDescription, open ? descriptionId : null].filter(Boolean).join(" ");
  useEffect(() => () => clearTimeout(pointerTimer.current), []);
  useEffect(() => {
    if (!open) return;
    const dismiss = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      // A hint must dismiss before an enclosing Dialog/Menu handles Escape.
      event.preventDefault();
      event.stopImmediatePropagation();
      clearTimeout(pointerTimer.current);
      dismissed.current = true;
      setOpen(false);
    };
    document.addEventListener("keydown", dismiss, true);
    return () => document.removeEventListener("keydown", dismiss, true);
  }, [open]);
  useLayoutEffect(() => {
    if (!open) return;
    const root = document.documentElement;
    const measure = () => setPageScale(Number.parseFloat(getComputedStyle(root).zoom) || 1);
    measure();
    // Only the visible tooltip observes layout, including CSS accessibility zoom.
    const observer = new ResizeObserver(measure);
    observer.observe(root);
    return () => observer.disconnect();
  }, [open]);

  function prepareOpen() {
    if (disabled || dismissed.current) return false;
    const element = trigger.current;
    if (!element) return false;
    const reason = element.getAttribute("data-tooltip-disabled-reason");
    // Measure overflow on demand, without an observer for every collection row.
    if (
      element.getAttribute("aria-expanded") === "true" ||
      (onlyWhenTruncated &&
        !reason &&
        element.scrollWidth <= element.clientWidth + 1 &&
        element.scrollHeight <= element.clientHeight + 1)
    )
      return false;
    setOverride(reason);
    setUnavailable(element.matches(":disabled, [aria-disabled='true'], [data-disabled]"));
    return true;
  }
  if (!hasContent) return children;

  return (
    <BaseTooltip.Root
      open={open}
      disabled={disabled}
      onOpenChange={(open, details) => {
        if (!open) {
          clearTimeout(pointerTimer.current);
          setOpen(false);
          return;
        }
        if (!prepareOpen()) {
          details.cancel();
          return;
        }
        setOpen(true);
      }}
    >
      <TriggerContext.Provider value={true}>
        <BaseTooltip.Trigger
          ref={(element: HTMLElement | null) => {
            trigger.current = element;
          }}
          render={children as ReactElement<Record<string, unknown>>}
          data-tooltip-trigger
          className={styles.trigger ?? ""}
          {...(shortcutAria ? { "aria-keyshortcuts": shortcutAria } : {})}
          {...(describedBy ? { "aria-describedby": describedBy } : {})}
          onFocus={(event) => {
            dismissed.current = false;
            // The pinned Base UI release can retain its Escape focus guard
            // after hovering the popup. A new keyboard focus must still open.
            if (event.currentTarget.matches(":focus-visible") && prepareOpen()) setOpen(true);
          }}
          onPointerEnter={(event) => {
            dismissed.current = false;
            // Native disabled buttons suppress mouse events, but retain pointer
            // events. Keep native disabled semantics and avoid layout wrappers.
            if (event.pointerType !== "mouse" || !event.currentTarget.matches(":disabled")) return;
            clearTimeout(pointerTimer.current);
            pointerTimer.current = setTimeout(() => {
              if (prepareOpen()) setOpen(true);
            }, 400);
          }}
          onPointerLeave={(event) => {
            dismissed.current = false;
            if (!event.currentTarget.matches(":disabled")) return;
            clearTimeout(pointerTimer.current);
            if (event.relatedTarget instanceof Node && popup.current?.contains(event.relatedTarget))
              return;
            pointerTimer.current = setTimeout(() => setOpen(false), 80);
          }}
        />
      </TriggerContext.Provider>
      <BaseTooltip.Portal>
        <BaseTooltip.Positioner
          positionMethod="fixed"
          side={side}
          align={align}
          sideOffset={7}
          collisionPadding={12}
          className={styles.positioner ?? ""}
          style={{ "--tooltip-page-scale": pageScale } as CSSProperties}
        >
          <BaseTooltip.Popup
            ref={popup}
            id={descriptionId}
            role="tooltip"
            className={styles.popup ?? ""}
            onPointerEnter={() => clearTimeout(pointerTimer.current)}
            onPointerLeave={() => {
              if (trigger.current?.matches(":disabled")) setOpen(false);
            }}
          >
            <span className={styles.label}>{override ?? content}</span>
            {!override && !unavailable && shortcuts?.length ? (
              <span className={styles.shortcuts} aria-hidden="true">
                {shortcuts.map((keys, index) => (
                  <span className={styles.alternative} key={index}>
                    {index > 0 ? <span className={styles.or}>/</span> : null}
                    <span className={styles.chord}>
                      {keys.map((key, keyIndex) => (
                        <Kbd key={keyIndex} className={styles.key ?? ""}>
                          {key}
                        </Kbd>
                      ))}
                    </span>
                  </span>
                ))}
              </span>
            ) : null}
          </BaseTooltip.Popup>
        </BaseTooltip.Positioner>
      </BaseTooltip.Portal>
    </BaseTooltip.Root>
  );
}
